import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, quantitySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
/**
 * Endeks (totalizer) sürekliliği, endeks raporu ve ONAYLI elle düzeltme (Prompt 12 §7; ADR 0012 §12).
 *
 * Endeks yalnızca ARTAR. Azalan okuma taşma (rollover) ya da anomalidir; anomali satışa girmez ve tabancanın
 * anlık endeksini İLERLETMEZ — ham okuma yine de yazılır (ölçü kaydı silinmez / reddedilmez, AGENTS.md §3.5).
 * Sayaç değişimi / sıfırlama / düzeltme yalnızca **gerekçeli ve onaylı** bir düzeltme belgesiyle yapılır; onay
 * sonrası `ADJUSTMENT` kaynaklı yeni bir okuma yazılır ve endeks serisi o noktada BÖLÜNÜR.
 */
export const TOTALIZER_ADJUSTMENT_KINDS = ["METER_REPLACED", "RESET", "CORRECTION"];
export const totalizerAdjustmentKindSchema = z.enum(TOTALIZER_ADJUSTMENT_KINDS);
export const TOTALIZER_ADJUSTMENT_KIND_LABELS = {
    METER_REPLACED: "Sayaç değişti",
    RESET: "Sayaç sıfırlandı",
    CORRECTION: "Yanlış okuma düzeltmesi",
};
export const TOTALIZER_ADJUSTMENT_STATUSES = [
    "PENDING_APPROVAL",
    "APPROVED",
    "REJECTED",
    "CANCELLED",
];
export const totalizerAdjustmentStatusSchema = z.enum(TOTALIZER_ADJUSTMENT_STATUSES);
export const TOTALIZER_ADJUSTMENT_STATUS_LABELS = {
    PENDING_APPROVAL: "Onay bekliyor",
    APPROVED: "Onaylandı",
    REJECTED: "Reddedildi",
    CANCELLED: "İptal edildi",
};
export const totalizerAdjustmentSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    nozzleId: uuidSchema,
    /** "1-2" — pompa no ve tabanca no (ekranda kimlik yerine bu gösterilir). */
    nozzleLabel: z.string(),
    productName: z.string(),
    effectiveAt: timestampSchema,
    previousElectronicL: quantitySchema.describe("Yürürlük anındaki SON okuma (belgede dondurulur)"),
    newElectronicL: quantitySchema,
    newMechanicalL: quantitySchema.nullable(),
    kind: totalizerAdjustmentKindSchema,
    reason: z.string(),
    status: totalizerAdjustmentStatusSchema,
    approvalRequestId: uuidSchema.nullable(),
    requestedBy: uuidSchema.nullable(),
    decidedAt: timestampSchema.nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
});
export const createTotalizerAdjustmentSchema = z.object({
    nozzleId: uuidSchema,
    kind: totalizerAdjustmentKindSchema,
    newElectronicL: quantitySchema,
    newMechanicalL: quantitySchema.nullable().optional(),
    effectiveAt: timestampSchema.describe("Gelecekte olamaz"),
    reason: z.string().trim().min(5).max(1000).describe("Gerekçe zorunludur (en az 5 karakter)"),
});
export const cancelTotalizerAdjustmentSchema = z.object({
    version: z.number().int().min(1),
});
export const listTotalizerAdjustmentsQuerySchema = pageQuerySchema.extend({
    stationId: uuidSchema.optional(),
    nozzleId: uuidSchema.optional(),
    status: totalizerAdjustmentStatusSchema.optional(),
});
export const totalizerAdjustmentPageSchema = pageOf(totalizerAdjustmentSchema);
export const totalizerAdjustmentParamsSchema = z.object({ id: uuidSchema });
// ── Endeks raporu ────────────────────────────────────────────────────────────
export const totalizerReportQuerySchema = z.object({
    stationId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema.describe("Dâhil (gün sonuna kadar)"),
});
export const totalizerReportRowSchema = z.object({
    nozzleId: uuidSchema,
    nozzleLabel: z.string(),
    dispenserNo: z.number().int(),
    nozzleNo: z.number().int(),
    productId: uuidSchema,
    productName: z.string(),
    firstElectronicL: quantitySchema.nullable(),
    firstReadAt: timestampSchema.nullable(),
    lastElectronicL: quantitySchema.nullable(),
    lastReadAt: timestampSchema.nullable(),
    /** Taşma güvenli, ONAYLI DÜZELTME noktalarında BÖLÜNMÜŞ seriden. */
    totalizerSalesL: quantitySchema,
    /** İşlem (pompa satışı) toplamı — test çekişleri DÂHİL (endeks testi de sayar). */
    transactionSalesL: quantitySchema,
    differenceL: quantitySchema.describe("endeks satışı − işlem satışı"),
    rollovers: z.number().int().nonnegative(),
    anomalies: z.number().int().nonnegative(),
    readingCount: z.number().int().nonnegative(),
    adjustments: z.number().int().nonnegative(),
    /** Elektronik ↔ mekanik ARTIŞ farkı; mekanik okuma yoksa null. */
    mechanicalDifferenceL: quantitySchema.nullable(),
});
export const totalizerReportSchema = z.object({
    stationId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
    rows: z.array(totalizerReportRowSchema),
});
//# sourceMappingURL=totalizers.js.map